import { useCallback, useEffect } from "react";
import {
  useNavigate,
  useSearchParams,
  type NavigateFunction,
} from "react-router";
import { formatIsoDate, parseIsoDate } from "../dateTime";
import { encodeSearchParamsForUrl } from "../encodeSearchParamsForUrl";
import { useTypedLocation, type TypedLocation } from "./useTypedLocation";

export type GlobalDates = {
  departureDate?: Date;
  returnDate?: Date;
};

const DEPARTURE_PARAM_KEY = "departureDate";
export const RETURN_PARAM_KEY = "returnDate";

export function useDateURL() {
  const location = useTypedLocation();
  const navigate = useNavigate();
  // Because of our custom encodeSearchParamsForUrl logic,
  // we cannot rely on setSearchParams and must navigate manually
  const [searchParams] = useSearchParams();
  const departureParam = searchParams.get(DEPARTURE_PARAM_KEY);
  const returnParam = searchParams.get(RETURN_PARAM_KEY);

  const setGlobalDates = useCallback(
    (datesOrUpdater: GlobalDates | ((prev: GlobalDates) => GlobalDates)) => {
      const currentDates: GlobalDates = {
        departureDate: departureParam
          ? parseIsoDate(departureParam)
          : undefined,
        returnDate: returnParam ? parseIsoDate(returnParam) : undefined,
      };

      const newDates =
        typeof datesOrUpdater === "function"
          ? datesOrUpdater(currentDates)
          : datesOrUpdater;

      const { cleanDates } = checkDateSanity(newDates);
      setDatesAndNavigate(navigate, cleanDates, searchParams, location);
    },
    [departureParam, location, navigate, returnParam, searchParams],
  );

  const getGlobalDates = useCallback(() => {
    let departureDate = departureParam
      ? parseIsoDate(departureParam)
      : undefined;

    let returnDate = returnParam ? parseIsoDate(returnParam) : undefined;

    const { cleanDates, hasChanged } = checkDateSanity({
      departureDate,
      returnDate,
    });

    if (hasChanged) {
      setDatesAndNavigate(navigate, cleanDates, searchParams, location);
    }

    return cleanDates;
  }, [departureParam, location, navigate, returnParam, searchParams]);

  useEffect(() => {
    getGlobalDates();
  }, [getGlobalDates]);

  return {
    getGlobalDates,
    setGlobalDates,
  };
}

export function getDateParametersString(
  dates: GlobalDates,
  currentParams?: URLSearchParams,
): string | undefined {
  const newParams = new URLSearchParams(currentParams);

  if (dates.departureDate) {
    newParams.set(DEPARTURE_PARAM_KEY, formatIsoDate(dates.departureDate));
  } else {
    newParams.delete(DEPARTURE_PARAM_KEY);
  }
  if (dates.returnDate) {
    newParams.set(RETURN_PARAM_KEY, formatIsoDate(dates.returnDate));
  } else {
    newParams.delete(RETURN_PARAM_KEY);
  }

  if (newParams.toString() === "") {
    return undefined;
  } else {
    return encodeSearchParamsForUrl(newParams);
  }
}

function setDatesAndNavigate(
  navigate: NavigateFunction,
  dates: GlobalDates,
  searchParams: URLSearchParams,
  location: TypedLocation,
) {
  const paramString = getDateParametersString(dates, searchParams);
  navigate(
    { ...location, search: paramString },
    { state: location.state, replace: true },
  );
}

function checkDateSanity(dates: GlobalDates): {
  cleanDates: GlobalDates;
  hasChanged: boolean;
} {
  let hasChanged = false;
  let { departureDate, returnDate } = dates;

  if (returnDate && departureDate && returnDate < departureDate) {
    console.warn("Return date cannot be before departure date");
    returnDate = undefined;
    hasChanged = true;
  }

  const currentDate = new Date();
  if (
    departureDate &&
    formatIsoDate(departureDate) < formatIsoDate(currentDate)
  ) {
    console.warn("Departure date cannot be in the past");
    departureDate = undefined;
    returnDate = undefined;
    hasChanged = true;
  }
  return { cleanDates: { departureDate, returnDate }, hasChanged };
}
